import React, { useEffect, useState, useRef } from "react";
import { Button, Spinner, OverlayTrigger, Tooltip, Table, Badge } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import EditAttendanceModal from "./EditAttendanceModal";
import moment from "moment";
import { API_URL } from "../..";
import TimesheetPayrollSlip from "./TimesheetPayrollSlip";
import TimesheetPayrollSlipWeekly from "./TimesheetPayrollSlipWeekly";

const TimesheetWeeklyView = ({ date, employee }) => {
    
const [data, setData] = useState([]);
const [loading, setLoading] = useState(false);
const [editRecord, setEditRecord] = useState(null);
const tableRef = useRef();

// Current week (Mon–Sun)
const startOfWeek = moment(date).startOf("week").add(1, "day");
const endOfWeek = moment(startOfWeek).clone().add(6, "days");
const today = moment().startOf("day");

const weekDays = Array.from({ length: 7 }, (_, i) =>
moment(startOfWeek).clone().add(i, "days")
);

// Fetch weekly attendance
const fetchWeekly = async () => {
try {
    setLoading(true);
    const query = new URLSearchParams();
    query.append("startDate", startOfWeek.format("YYYY-MM-DD"));
    query.append("endDate", endOfWeek.format("YYYY-MM-DD"));
    if (employee) query.append("employeeId", employee);

    const res = await fetch(`${API_URL}/api/attendance/timesheet-log/weekly?${query}`);
    const json = await res.json();
    if (json.success) setData(json.data || []);
} catch (err) {
    console.error("Weekly fetch error:", err);
} finally {
    setLoading(false);
}
};

useEffect(() => {
fetchWeekly();
}, [date, employee]);

// PDF Export
const handleExportPDF = async () => {
const input = tableRef.current;
const pdf = new jsPDF("l", "pt", "a4");
const canvas = await html2canvas(input, { scale: 1.3 });
const imgData = canvas.toDataURL("image/png");
const imgWidth = 800;
const imgHeight = (canvas.height * imgWidth) / canvas.width;

pdf.setFontSize(18);
pdf.text(
    `Weekly Timesheet (${startOfWeek.format("DD MMM")} - ${endOfWeek.format("DD MMM YYYY")})`,
    40,
    30
);
pdf.addImage(imgData, "PNG", 40, 50, imgWidth, imgHeight);
pdf.save(`Timesheet_Week_${startOfWeek.format("YYYYMMDD")}.pdf`);
};

// Format time display
const formatTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm A") : "--");

// Get background color based on status
const getColorClass = (status, date) => {
const current = moment(date).startOf("day");
if (current.isAfter(today)) return "bg-light text-muted"; // upcoming
switch (status) {
    case "late":
    return "bg-warning text-dark";
    case "absent":
    return "bg-danger text-white";
    case "early":
    case "on-time":
    return "bg-success text-white";
    default:
    return "bg-secondary text-white";
}
};

// Determine editability
const canEdit = (day) => !moment(day).isAfter(today);
if (employee && data && data.user) {
 
if(loading){
    return (
        <div className="text-center py-4">
    <Spinner animation="border" /> Loading...
    </div>
    );
}   
return <TimesheetPayrollSlipWeekly  employee={employee} date={date} data={data} />
}
return (
<div className="card shadow-sm">
    <div className="card-header d-flex justify-content-between align-items-center">
    <h5 className="mb-0">
        Weekly Timesheet ({startOfWeek.format("DD MMM")} - {endOfWeek.format("DD MMM YYYY")})
    </h5>
    <Button variant="outline-primary" onClick={handleExportPDF}>
        <i className="bi bi-file-earmark-pdf me-2"></i>Download PDF
    </Button>
    </div>

    <div className="card-body p-3" ref={tableRef}>
    {loading ? (
        <div className="text-center py-4">
        <Spinner animation="border" /> Loading...
        </div>
    ) : (
        <div className="table-responsive">
        <Table bordered hover className="align-middle text-center">
            <thead className="table-light">
            <tr>
                <th style={{ width: 200 }}>Employee</th>
                {weekDays.map((d) => (
                <th key={d.format("YYYY-MM-DD")}>{d.format("ddd DD")}</th>
                ))}
            </tr>
            </thead>
            <tbody>
            {data.length > 0 ? (
                data.map((emp) => (
                <tr key={emp.user.id}>
                    <td className="fw-semibold text-start">{emp.user?.name}</td>
                    {weekDays.map((day) => {
                    const dayStr = day.format("YYYY-MM-DD");
                    const rec = emp.attendance?.find(
                        (r) => r.date === dayStr || r.attendance_date === dayStr
                    );
                    
                        // rec.user_id = emp?.user?.id
                    

                    const overtime = rec?.attendanceOvertimes?.[0];
                    const isFuture = moment(dayStr).isAfter(today);

                    return (
                        <td
                        key={dayStr}
                        className={`${getColorClass(rec?.status, dayStr)} small position-relative`}
                        style={{
                            cursor: canEdit(dayStr) ? "pointer" : "not-allowed",
                            minWidth: rec?.status === "absent" ? "60px" : "120px",
                            padding: rec?.status === "absent" ? "6px" : "8px",
                        }}
                        onClick={() => {
                            
                            if (canEdit(dayStr)) setEditRecord(rec || { user_id: emp.user.id, date: dayStr });
                        }}
                        >
                        {isFuture ? (
                            <Badge bg="light" text="dark">
                            Upcoming
                            </Badge>
                        ) : rec ? (
                            <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                <div style={{ textAlign: "left" }}>
                                    <strong>Status:</strong> {rec.status == 'on-time' || rec.status == 'early' ? 'PRESENT' : String(rec.status).toUpperCase() || "N/A"} <br />
                                    <strong>Punch In:</strong> {formatTime(rec.punch_in_time)} <br />
                                    <strong>Punch Out:</strong> {formatTime(rec.punch_out_time)} <br />
                                    <strong>Work:</strong> {rec.total_work_hours || "0"} hrs <br />
                                    <strong>Break:</strong> {rec.total_break_hours || "0"} hrs
                                    {overtime && (
                                    <>
                                        <br />
                                        <span><strong>Overtime hour:</strong>{" "}{overtime.total_duration}</span>
                                    </>
                                    )}
                                </div>
                                </Tooltip>
                            }
                            >
                            <div>
                                <div className="fw-bold">
                                {formatTime(rec.punch_in_time)} - {formatTime(rec.punch_out_time)}
                                </div>
                                <small className="text-uppercase">{rec.status == 'ABSENT' ? 'A' :rec.status  === 'on-time' || rec.status ==='early' ? 'PRESENT' : String(rec.status).toUpperCase()}</small>
                            </div>
                            </OverlayTrigger>
                        ) : (
                            <div className="fw-bold text-white">A</div>
                        )}
                        </td>
                    );
                    })}
                </tr>
                ))
            ) : (
                <tr>
                <td colSpan={8} className="text-center py-4">
                    No attendance found for this week.
                </td>
                </tr>
            )}
            </tbody>
        </Table>
        </div>
    )}
    </div>

    {/* Edit modal */}
    {editRecord && (
    <EditAttendanceModal
        record={editRecord}
        employee={employee}
        
        onClose={() => setEditRecord(null)}
        onUpdated={(updatedRecord) => {
        setData((prev) => {
            const newData = [...prev];
            newData.forEach((emp) => {
            if (emp.user.id === updatedRecord.user_id) {
                const idx = emp.attendance.findIndex(
                (r) => r.id === updatedRecord.id || r.date === updatedRecord.attendance_date
                );
        
                if (idx > -1) {
                emp.attendance[idx] = updatedRecord;
                } else {
                emp.attendance.push(updatedRecord);
                }
            }
            });
            return newData;
        });
        }}
    />
    )}
</div>
);
};

export default TimesheetWeeklyView;
